import PizZip from 'pizzip';
import { saveAs } from 'file-saver';

/**
 * Minimal .xlsx (OOXML SpreadsheetML) writer built on PizZip, the zip library
 * already used by the .docx exports. Produces a real workbook - numeric cells
 * stay numeric so Excel can sum them - without pulling in a new dependency.
 */

export type XlsxVariant = 'normal' | 'bold' | 'header' | 'total';

/** Number format. 'decimal' renders as #,##0.00, 'general' leaves it alone. */
export type XlsxFormat = 'general' | 'decimal';

export interface XlsxCell {
    /** string renders as text, number renders as a real numeric cell. */
    value?: string | number;
    /** Defaults to 'normal'. */
    variant?: XlsxVariant;
    /** Defaults to 'general', so integers stay integers. */
    format?: XlsxFormat;
}

/** A bare string or number is shorthand for `{ value, variant: 'normal' }`. */
export type XlsxCellInput = XlsxCell | string | number | undefined;

export interface XlsxColumn {
    /** Excel character-width units. Omit to keep the default width. */
    width?: number;
}

export interface XlsxSheet {
    /** Sanitized automatically: max 31 chars, forbidden characters stripped. */
    name: string;
    rows: XlsxCellInput[][];
    /** Positional, index 0 is column A. */
    columns?: XlsxColumn[];
    /** Number of leading rows to freeze. 0 or omitted means no frozen pane. */
    freezeRows?: number;
}

const XML_HEADER = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';

const XLSX_MIME =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const RELS_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';
const REL_BASE = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const CONTENT_TYPES_NS = 'http://schemas.openxmlformats.org/package/2006/content-types';
const SPREADSHEET_NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const SPREADSHEET_CT = 'application/vnd.openxmlformats-officedocument.spreadsheetml';

/**
 * Excel is strict about this part: the child order of styleSheet is fixed,
 * every `count` must match, fills 0 and 1 must be `none` and `gray125`, and a
 * custom numFmtId must be >= 164. The cellXfs order below is what styleIndex()
 * maps onto - keep the two in sync.
 */
const STYLES_XML =
    XML_HEADER +
    '<styleSheet xmlns="' + SPREADSHEET_NS + '">' +
    '<numFmts count="1"><numFmt numFmtId="164" formatCode="#,##0.00"/></numFmts>' +
    '<fonts count="2">' +
    '<font><sz val="11"/><color rgb="FF000000"/><name val="Calibri"/></font>' +
    '<font><b/><sz val="11"/><color rgb="FF000000"/><name val="Calibri"/></font>' +
    '</fonts>' +
    '<fills count="3">' +
    '<fill><patternFill patternType="none"/></fill>' +
    '<fill><patternFill patternType="gray125"/></fill>' +
    '<fill><patternFill patternType="solid"><fgColor rgb="FFD9E1F2"/><bgColor indexed="64"/></patternFill></fill>' +
    '</fills>' +
    '<borders count="2">' +
    '<border><left/><right/><top/><bottom/><diagonal/></border>' +
    '<border><left/><right/><top style="thin"><color rgb="FF000000"/></top><bottom/><diagonal/></border>' +
    '</borders>' +
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
    '<cellXfs count="8">' +
    '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
    '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
    '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>' +
    '<xf numFmtId="164" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyNumberFormat="1"/>' +
    '<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/>' +
    '<xf numFmtId="164" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyNumberFormat="1"/>' +
    '<xf numFmtId="0" fontId="1" fillId="0" borderId="1" xfId="0" applyFont="1" applyBorder="1"/>' +
    '<xf numFmtId="164" fontId="1" fillId="0" borderId="1" xfId="0" applyFont="1" applyBorder="1" applyNumberFormat="1"/>' +
    '</cellXfs>' +
    '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
    '<dxfs count="0"/>' +
    '<tableStyles count="0" defaultTableStyle="TableStyleMedium2" defaultPivotStyle="PivotStyleLight16"/>' +
    '</styleSheet>';

const ROOT_RELS_XML =
    XML_HEADER +
    '<Relationships xmlns="' + RELS_NS + '">' +
    '<Relationship Id="rId1" Type="' + REL_BASE + '/officeDocument" Target="xl/workbook.xml"/>' +
    '</Relationships>';

/** Strips characters illegal in XML 1.0 before escaping the rest. */
function escapeXml(value: string): string {
    return value
        .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

/** 0 -> A, 25 -> Z, 26 -> AA (bijective base 26). */
function columnName(index: number): string {
    let remaining = index + 1;
    let name = '';
    while (remaining > 0) {
        const rest = (remaining - 1) % 26;
        name = String.fromCharCode(65 + rest) + name;
        remaining = Math.floor((remaining - 1) / 26);
    }
    return name;
}

function sanitizeSheetName(name: string, fallback: string): string {
    let clean = String(name || '').replace(/[\\\/\?\*\[\]:]/g, '').trim();
    if (clean.length > 31) clean = clean.substring(0, 31);
    // Truncation can re-expose a quote at either end, so strip after cutting.
    clean = clean.replace(/^'+/, '').replace(/'+$/, '').trim();
    return clean || fallback;
}

function uniqueSheetNames(sheets: XlsxSheet[]): string[] {
    const used: { [key: string]: boolean } = {};
    return sheets.map((sheet: XlsxSheet, index: number) => {
        const base = sanitizeSheetName(sheet.name, 'Sheet' + (index + 1));
        let candidate = base;
        let attempt = 2;
        while (used[candidate.toLowerCase()]) {
            const suffix = ' (' + attempt + ')';
            candidate = base.substring(0, 31 - suffix.length) + suffix;
            attempt++;
        }
        used[candidate.toLowerCase()] = true;
        return candidate;
    });
}

function normalizeCell(input: XlsxCellInput): XlsxCell {
    if (input === undefined) return {};
    if (typeof input === 'string' || typeof input === 'number') {
        return { value: input };
    }
    return input;
}

/** Must match the cellXfs order in STYLES_XML. */
function styleIndex(variant: XlsxVariant, format: XlsxFormat): number {
    let base = 0;
    if (variant === 'bold') base = 2;
    else if (variant === 'header') base = 4;
    else if (variant === 'total') base = 6;
    return base + (format === 'decimal' ? 1 : 0);
}

/** Excel rejects exponent notation such as "1e+21". */
function numberToXml(value: number): string {
    if (!isFinite(value)) return '0';
    const text = String(value);
    if (text.indexOf('e') === -1 && text.indexOf('E') === -1) return text;
    return value.toFixed(6);
}

function buildCellXml(input: XlsxCellInput, ref: string): string {
    const cell = normalizeCell(input);
    const variant = cell.variant || 'normal';
    const value = cell.value;

    if (value === undefined || value === '') {
        // Empty but styled cells still have to exist, otherwise fills and
        // borders stop short of the end of the row.
        if (variant === 'normal') return '';
        return '<c r="' + ref + '" s="' + styleIndex(variant, 'general') + '"/>';
    }

    const style = styleIndex(variant, cell.format || 'general');

    if (typeof value === 'number') {
        return (
            '<c r="' + ref + '" s="' + style + '">' +
            '<v>' + numberToXml(value) + '</v></c>'
        );
    }

    const text = String(value);
    const preserve = /^\s|\s$/.test(text) ? ' xml:space="preserve"' : '';
    return (
        '<c r="' + ref + '" s="' + style + '" t="inlineStr">' +
        '<is><t' + preserve + '>' + escapeXml(text) + '</t></is></c>'
    );
}

function buildSheetXml(sheet: XlsxSheet): string {
    const rows = sheet.rows || [];
    let columnCount = 0;
    rows.forEach((row: XlsxCellInput[]) => {
        if (row && row.length > columnCount) columnCount = row.length;
    });

    const parts: string[] = [XML_HEADER, '<worksheet xmlns="' + SPREADSHEET_NS + '">'];

    const lastRef =
        columnCount && rows.length
            ? columnName(columnCount - 1) + rows.length
            : 'A1';
    parts.push('<dimension ref="A1:' + lastRef + '"/>');

    const freeze = sheet.freezeRows || 0;
    if (freeze > 0) {
        const topLeft = 'A' + (freeze + 1);
        parts.push(
            '<sheetViews><sheetView tabSelected="1" workbookViewId="0">' +
            '<pane ySplit="' + freeze + '" topLeftCell="' + topLeft + '" activePane="bottomLeft" state="frozen"/>' +
            '<selection pane="bottomLeft" activeCell="' + topLeft + '" sqref="' + topLeft + '"/>' +
            '</sheetView></sheetViews>',
        );
    }

    parts.push('<sheetFormatPr defaultRowHeight="15"/>');

    const columnParts: string[] = [];
    (sheet.columns || []).forEach((column: XlsxColumn, index: number) => {
        if (!column || !column.width) return;
        const position = index + 1;
        columnParts.push(
            '<col min="' + position + '" max="' + position +
            '" width="' + column.width + '" customWidth="1"/>',
        );
    });
    // An empty <cols/> element makes Excel offer to repair the file.
    if (columnParts.length) {
        parts.push('<cols>' + columnParts.join('') + '</cols>');
    }

    parts.push('<sheetData>');
    rows.forEach((row: XlsxCellInput[], rowIndex: number) => {
        const rowNumber = rowIndex + 1;
        const cellParts: string[] = [];
        (row || []).forEach((input: XlsxCellInput, columnIndex: number) => {
            const xml = buildCellXml(input, columnName(columnIndex) + rowNumber);
            if (xml) cellParts.push(xml);
        });
        parts.push(
            cellParts.length
                ? '<row r="' + rowNumber + '">' + cellParts.join('') + '</row>'
                : '<row r="' + rowNumber + '"/>',
        );
    });
    parts.push('</sheetData>');
    parts.push('</worksheet>');

    return parts.join('');
}

function buildWorkbookXml(names: string[]): string {
    const sheetParts = names.map(
        (name: string, index: number) =>
            '<sheet name="' + escapeXml(name) + '" sheetId="' + (index + 1) +
            '" r:id="rId' + (index + 1) + '"/>',
    );
    return (
        XML_HEADER +
        '<workbook xmlns="' + SPREADSHEET_NS + '" xmlns:r="' + REL_BASE + '">' +
        '<sheets>' + sheetParts.join('') + '</sheets>' +
        '</workbook>'
    );
}

function buildWorkbookRelsXml(sheetCount: number): string {
    const parts: string[] = [];
    for (let index = 1; index <= sheetCount; index++) {
        parts.push(
            '<Relationship Id="rId' + index + '" Type="' + REL_BASE +
            '/worksheet" Target="worksheets/sheet' + index + '.xml"/>',
        );
    }
    parts.push(
        '<Relationship Id="rId' + (sheetCount + 1) + '" Type="' + REL_BASE +
        '/styles" Target="styles.xml"/>',
    );
    return (
        XML_HEADER +
        '<Relationships xmlns="' + RELS_NS + '">' + parts.join('') + '</Relationships>'
    );
}

function buildContentTypesXml(sheetCount: number): string {
    const parts: string[] = [
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>',
        '<Default Extension="xml" ContentType="application/xml"/>',
        '<Override PartName="/xl/workbook.xml" ContentType="' + SPREADSHEET_CT + '.sheet.main+xml"/>',
        '<Override PartName="/xl/styles.xml" ContentType="' + SPREADSHEET_CT + '.styles+xml"/>',
    ];
    for (let index = 1; index <= sheetCount; index++) {
        parts.push(
            '<Override PartName="/xl/worksheets/sheet' + index +
            '.xml" ContentType="' + SPREADSHEET_CT + '.worksheet+xml"/>',
        );
    }
    return (
        XML_HEADER +
        '<Types xmlns="' + CONTENT_TYPES_NS + '">' + parts.join('') + '</Types>'
    );
}

/** Builds the .xlsx package and returns it as a Blob. */
export function buildXlsxBlob(sheets: XlsxSheet[]): Blob {
    const list: XlsxSheet[] =
        sheets && sheets.length ? sheets : [{ name: 'Sheet1', rows: [] }];
    const names = uniqueSheetNames(list);
    const zip = new PizZip();

    zip.file('[Content_Types].xml', buildContentTypesXml(list.length));
    zip.file('_rels/.rels', ROOT_RELS_XML);
    zip.file('xl/workbook.xml', buildWorkbookXml(names));
    zip.file('xl/_rels/workbook.xml.rels', buildWorkbookRelsXml(list.length));
    zip.file('xl/styles.xml', STYLES_XML);
    list.forEach((sheet: XlsxSheet, index: number) => {
        zip.file('xl/worksheets/sheet' + (index + 1) + '.xml', buildSheetXml(sheet));
    });

    return zip.generate({
        type: 'blob',
        compression: 'DEFLATE',
        mimeType: XLSX_MIME,
    });
}

/** Builds the workbook and hands it to the browser as a download. */
export function saveXlsx(sheets: XlsxSheet[], fileName: string): void {
    saveAs(buildXlsxBlob(sheets), fileName);
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--2!./src/utils/xlsx.ts